import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { authApi } from "../api/authApi";
import { readStoredAuth, writeStoredAuth } from "./authStorage";
import { normalizeAegisHeroWebpUrl } from "../utils/aegisMedia";
const AUTH_TOKEN_UPDATED_EVENT = "bs:auth-token-updated";
const AUTH_INVALIDATED_EVENT = "bs:auth-invalidated";
const IS_REACT_SNAP = typeof navigator !== "undefined" && /ReactSnap/i.test(String(navigator.userAgent || ""));

function normalizeRole(role) {
    if (!role) return null;
    const raw =
        typeof role === "object"
            ? role?.title ?? role?.name ?? role?.role ?? role?.code ?? role?.authority ?? role?.value
            : role;
    if (!raw) return null;
    const s = String(raw).trim().toUpperCase();
    return s.startsWith("ROLE_") ? s.slice(5) : s; // ROLE_CLIENT -> CLIENT
}

function shouldBootstrapOnInitialLoad(storedAuth) {
    const hasStoredSession = Boolean(storedAuth?.token || storedAuth?.me);
    if (hasStoredSession) return true;
    if (IS_REACT_SNAP) return false;
    if (typeof window === "undefined") return false;

    const pathname = String(window.location.pathname || "");
    return /^(\/dashboard(?:\/.*)?|\/chat(?:\/.*)?|\/notifications(?:\/.*)?|\/login(?:\/.*)?|\/auth(?:\/.*)?|\/register(?:\/.*)?|\/recovery(?:\/.*)?|\/reset-password(?:\/.*)?|\/forgot-password(?:\/.*)?)/i.test(
        pathname
    );
}

const Ctx = createContext(null);

export function AuthProvider({ children }) {
    const [auth, setAuth] = useState(() => readStoredAuth()); // {token, me}
    const shouldBootRef = useRef(shouldBootstrapOnInitialLoad(readStoredAuth()));
    const [booting, setBooting] = useState(() => shouldBootRef.current);

    // защита от двойного boot/login в StrictMode / быстрых кликов
    const bootSeq = useRef(0);

    const setAuthSafe = useCallback((next) => {
        setAuth((prev) => {
            const resolved = typeof next === "function" ? next(prev) : next;
            writeStoredAuth(resolved);
            return resolved;
        });
    }, []);

    const applyMe = useCallback(
        (token, meRaw) => {
            if (!meRaw) return setAuthSafe({ token: token ?? null, me: null });
            const normalized = { ...meRaw, role: normalizeRole(meRaw.role) };
            const rawAvatar =
                normalized.avatarUrl ??
                normalized.avatar_url ??
                normalized.avatar ??
                normalized.photoUrl ??
                normalized.photo_url ??
                normalized.image ??
                "";
            const normalizedAvatar = normalizeAegisHeroWebpUrl(rawAvatar);
            if (normalizedAvatar) {
                normalized.avatarUrl = normalizedAvatar;
                if (normalized.avatar_url != null) normalized.avatar_url = normalizedAvatar;
                if (normalized.avatar != null) normalized.avatar = normalizedAvatar;
                if (normalized.photoUrl != null) normalized.photoUrl = normalizedAvatar;
                if (normalized.photo_url != null) normalized.photo_url = normalizedAvatar;
                if (normalized.image != null) normalized.image = normalizedAvatar;
            }
            setAuthSafe({ token: token ?? null, me: normalized });
            return normalized;
        },
        [setAuthSafe]
    );

    const boot = useCallback(async () => {
        const seq = ++bootSeq.current;
        setBooting(true);
        try {
            // refresh возвращает accessToken/token ИЛИ может вернуть вообще пусто, но выставить cookie
            const r = await authApi.refresh();
            const token = r?.token ?? r?.accessToken ?? r?.jwt ?? null;

            // если refresh не дал токен, всё равно пробуем me (cookie-based)
            const me = await authApi.me();
            if (seq !== bootSeq.current) return; // устаревший вызов
            applyMe(token, me);
        } catch {
            if (seq !== bootSeq.current) return;
            setAuthSafe(null);
        } finally {
            if (seq === bootSeq.current) setBooting(false);
        }
    }, [applyMe, setAuthSafe]);

    useEffect(() => {
        if (!shouldBootRef.current) {
            setBooting(false);
            return;
        }

        // один boot на монтировании
        boot();
    }, [boot]);

    useEffect(() => {
        const onTokenUpdated = (e) => {
            const nextToken = e?.detail?.token ?? null;
            setAuthSafe((prev) => {
                if (!prev) return prev;
                return { ...prev, token: nextToken };
            });
        };

        window.addEventListener(AUTH_TOKEN_UPDATED_EVENT, onTokenUpdated);
        return () => window.removeEventListener(AUTH_TOKEN_UPDATED_EVENT, onTokenUpdated);
    }, [setAuthSafe]);

    useEffect(() => {
        const onAuthInvalidated = () => {
            setAuthSafe(null);
        };

        window.addEventListener(AUTH_INVALIDATED_EVENT, onAuthInvalidated);
        return () => window.removeEventListener(AUTH_INVALIDATED_EVENT, onAuthInvalidated);
    }, [setAuthSafe]);

    const login = useCallback(
        async (email, password, captchaToken) => {
            const seq = ++bootSeq.current;
            setBooting(true);
            try {
                const r = await authApi.login(String(email || "").trim(), password, captchaToken);
                const token = r?.token ?? r?.accessToken ?? r?.jwt ?? null;
                if (token) {
                    writeStoredAuth({ token, me: null });
                }

                // token мог прийти хедером/кукой, me — отдельным запросом
                const me = await authApi.me();
                if (seq !== bootSeq.current) return null;
                return applyMe(token, me);
            } finally {
                if (seq === bootSeq.current) setBooting(false);
            }
        },
        [applyMe]
    );

    const loginByClientLink = useCallback(
        async (linkToken) => {
            const seq = ++bootSeq.current;
            setBooting(true);
            try {
                const r = await authApi.loginByClientLink(String(linkToken || "").trim());
                const token = r?.token ?? r?.accessToken ?? r?.jwt ?? null;
                if (token) {
                    writeStoredAuth({ token, me: null });
                }
                const me = await authApi.me();
                if (seq !== bootSeq.current) return null;
                const normalizedMe = applyMe(token, me);
                return {
                    ...(r && typeof r === "object" ? r : {}),
                    me: normalizedMe,
                    token,
                };
            } finally {
                if (seq === bootSeq.current) setBooting(false);
            }
        },
        [applyMe]
    );

    const logout = useCallback(async () => {
        const seq = ++bootSeq.current;
        setBooting(true);
        try {
            try {
                await authApi.logout();
            } catch {}
            if (seq !== bootSeq.current) return;
            setAuthSafe(null);
        } finally {
            if (seq === bootSeq.current) setBooting(false);
        }
    }, [setAuthSafe]);

    const isAuthenticated = !!auth?.me?.role;

    const value = useMemo(
        () => ({
            booting,
            auth,
            me: auth?.me || null,
            token: auth?.token || null,
            role: auth?.me?.role || null, // CLIENT/PSYCHOLOGIST/ADMIN
            isAuthenticated,
            boot,
            login,
            loginByClientLink,
            logout,
        }),
        [booting, auth, isAuthenticated, boot, login, loginByClientLink, logout]
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
    const v = useContext(Ctx);
    if (!v) throw new Error("useAuth must be used inside AuthProvider");
    return v;
}

export function hasRole(userRole, allowed = []) {
    if (!userRole) return false;
    return Array.isArray(allowed) && allowed.includes(userRole);
}
